import React from 'react';
import { Box, Typography, Chip, Paper, Link, IconButton } from '@mui/material';
import { MessageSquare, Briefcase, TrendingUp, Star, CheckCircle, Trash2, Award, UserCheck } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link as RouterLink } from 'react-router-dom';

interface TimelineItemProps {
    event: any;
    onDelete: (eventId: number) => void;
}

// Mapeamento de tipos de evento para ícones e cores
const eventTypeMap = {
    FEEDBACK: { icon: <MessageSquare size={20} />, color: '#3498db', label: 'Feedback' },
    DIRECTORATE_CHANGE: { icon: <Briefcase size={20} />, color: '#e67e22', label: 'Diretoria' },
    ROLE_LEVEL_CHANGE: { icon: <TrendingUp size={20} />, color: '#2ecc71', label: 'Cargo/Nível' },
    OTHER_EVENT: { icon: <Star size={20} />, color: '#9b59b6', label: 'Outro Evento' },
    // Ícones do seu print (exemplo)
    PROMOTION: { icon: <Award size={20} />, color: '#f1c40f', label: 'Promoção' },
    PERFORMANCE_EVALUATION: { icon: <UserCheck size={20} />, color: '#1abc9c', label: 'Avaliação' }
};

const TimelineItem: React.FC<TimelineItemProps> = ({ event, onDelete }) => {
    
    // Função para formatar a data
    const formattedDate = format(new Date(event.event_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });

    // Função para gerar o conteúdo do evento
    const renderEventDetails = () => {
        const details = event.details;
        // Combina todos os tipos de evento para gerar um título e tags
        const titles = [];
        const tags = [];
        
        if (event.event_types.includes('FEEDBACK')) {
            titles.push('Feedback');
            tags.push(eventTypeMap.FEEDBACK);
        }
        if (event.event_types.includes('DIRECTORATE_CHANGE')) {
            titles.push('Mudança de Diretoria');
            tags.push(eventTypeMap.DIRECTORATE_CHANGE);
        }
        if (event.event_types.includes('ROLE_LEVEL_CHANGE')) {
            titles.push('Promoção'); // ou 'Mudança de Cargo / Nível'
            tags.push(eventTypeMap.ROLE_LEVEL_CHANGE);
        }
        if (event.event_types.includes('OTHER_EVENT')) {
            titles.push(details.otherEvent?.title || 'Evento');
            tags.push(eventTypeMap.OTHER_EVENT);
        }

        // Simplesmente renderiza o resumo do feedback ou a descrição de outro evento como exemplo
        const descriptions = [];
        
        /*
        if (details.feedback?.summary) {
            descriptions.push(details.feedback.summary);
        }
        */
        if (details.directorateChange?.newDirectorateName) {
            descriptions.push(`Profissional movido para a diretoria de <strong>${details.directorateChange.newDirectorateName}</strong>.`);
        }
        if (details.roleLevelChange?.newRoleName) {
            const fromRole = details.roleLevelChange.previousRoleName || '';
            const fromLevel = details.roleLevelChange.previousLevelName || '';
            const toRole = details.roleLevelChange.newRoleName;
            const toLevel = details.roleLevelChange.newLevelName || '';
            
            if (fromRole) {
                descriptions.push(`Profissional promovido de <strong>${fromRole} ${fromLevel}</strong> para <strong>${toRole} ${toLevel}</strong>.`.trim());
            } else {
                descriptions.push(`Profissional alocado no cargo <strong>${toRole} ${toLevel}</strong>.`.trim());
            }
        }
        if (details.otherEvent?.description) {
            descriptions.push(details.otherEvent.description);
        }

        const fullDescription = descriptions.join('<br />');

        return {
            title: titles.join(' + '),
            tags,
            description: <Box dangerouslySetInnerHTML={{ __html: fullDescription }} sx={{'& p, & li, & strong': {margin: 0, padding: 0, display: 'inline'}}}/>
        };
    };

    const { tags, description } = renderEventDetails();
    // Usa o primeiro tipo de evento para o ícone principal
    const mainType = event.event_types[0] || 'OTHER_EVENT';
    const mainIconStyle = eventTypeMap[mainType] || eventTypeMap.OTHER_EVENT;

    return (
        <Box sx={{ display: 'flex', position: 'relative', mb: 1.5, zIndex: 2 }}>
            {/* Ícone à esquerda */}
            <Box sx={{
                width: 40,
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                pt: '1px'
            }}>
                <Paper
                    elevation={2}
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: mainIconStyle.color,
                        color: 'white'
                    }}
                >
                    {React.cloneElement(mainIconStyle.icon, { size: 14 })}
                </Paper>
            </Box>

            {/* Conteúdo do Card */}
            <Paper elevation={1} sx={{ flexGrow: 1, p: 1.25, borderRadius: '6px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    {formattedDate}
                </Typography>
                
                <Box sx={{ mt: 0.5, mb: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                    {description}
                </Box>

                {/* Tags e Link */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {tags.map(tag => (
                            <Chip
                                key={tag.label}
                                label={tag.label}
                                size="small"
                                sx={{ backgroundColor: `${tag.color}20`, color: tag.color, fontWeight: 'bold', fontSize: '0.65rem', height: '20px' }}
                                icon={<CheckCircle size={12} style={{ color: tag.color }} />}
                            />
                        ))}
                    </Box>
                    <Box>
                        <Link component={RouterLink} to={`/person-feed/view/${event.id}`} sx={{ fontWeight: 'bold', fontSize: '0.75rem', mr: 0.5 }}>
                            Ver Detalhes →
                        </Link>
                        <IconButton size="small" onClick={() => onDelete(event.id)} sx={{ padding: '4px' }}>
                            <Trash2 size={14} color="red" />
                        </IconButton>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default TimelineItem;
